// =============================================================================
// RMA Status & Enums
// =============================================================================

export type RmaStatus = 'draft' | 'label_generated' | 'shipped' | 'received' | 'inspecting' | 'resolved' | 'closed';

export type RmaReason = 'defective' | 'damaged' | 'wrong_item' | 'calibration' | 'end_of_life' | 'other';

export type RmaResolution = 'repaired' | 'replaced' | 'scrapped';

export type ItemCondition = 'untested' | 'functional' | 'defective' | 'damaged';

export type ItemResolution = 'repaired' | 'replaced' | 'scrapped';

export type CpServiceCode = 'DOM.RP' | 'DOM.EP' | 'DOM.XP' | 'DOM.PC';

// =============================================================================
// RMA Order
// =============================================================================

export interface RmaOrder {
  id: string;
  rmaNumber: string;
  accountId: string;
  accountName: string | null;
  siteId: string | null;
  quoteId: string | null;
  quoteNumber: string | null;
  status: RmaStatus;
  reason: RmaReason;
  reasonNotes: string | null;
  resolution: RmaResolution | null;
  resolutionNotes: string | null;
  returnAddressName: string;
  returnAddressCompany: string | null;
  returnAddressLine1: string;
  returnAddressLine2: string | null;
  returnAddressCity: string;
  returnAddressProvince: string;
  returnAddressPostalCode: string;
  shipFromName: string | null;
  shipFromCompany: string | null;
  shipFromLine1: string | null;
  shipFromLine2: string | null;
  shipFromCity: string | null;
  shipFromProvince: string | null;
  shipFromPostalCode: string | null;
  cpTrackingPin: string | null;
  cpLabelUrl: string | null;
  cpServiceCode: CpServiceCode | null;
  cpPublicKeyUrl: string | null;
  cpQrCode: string | null;
  /** Carrier the return label was minted with; NULL = legacy Canada Post row. */
  carrier?: 'canada_post' | 'planete_courrier' | null;
  pcShipmentNumber: string | null;
  pcServiceName: string | null;
  pcPriceTotal: number | string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  resolvedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  items: RmaItem[];
  activity: RmaActivityEntry[];
}

export interface RmaListItem {
  id: string;
  rmaNumber: string;
  accountId: string;
  accountName: string | null;
  quoteNumber: string | null;
  status: RmaStatus;
  reason: RmaReason;
  cpTrackingPin: string | null;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
}

// =============================================================================
// RMA Items
// =============================================================================

export interface RmaItem {
  id: string;
  rmaOrderId: string;
  serializedItemId: string | null;
  serialNumber: string | null;
  sku: string;
  productName: string | null;
  quantity: number;
  reason: RmaReason | null;
  conditionOnReceipt: ItemCondition | null;
  itemResolution: ItemResolution | null;
  itemNotes: string | null;
  createdAt: string;
}

// =============================================================================
// Activity Log
// =============================================================================

export interface RmaActivityEntry {
  id: string;
  rmaOrderId: string;
  action: string;
  details: Record<string, unknown> | null;
  performedBy: string | null;
  createdAt: string;
}

// =============================================================================
// API Request/Response Types
// =============================================================================

export interface RmaListResponse {
  items: RmaListItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export interface RmaFilters {
  status?: RmaStatus;
  accountId?: string;
  search?: string;
  pageNumber?: number;
  pageLength?: number;
  sortKey?: string;
  sortDirection?: 1 | -1;
}

export interface CreateRmaInput {
  accountId: string;
  siteId?: string;
  quoteId?: string;
  quoteNumber?: string;
  reason?: RmaReason;
  reasonNotes?: string;
  resolution?: RmaResolution;
  cpServiceCode?: CpServiceCode;
  shipFromName?: string | null;
  shipFromCompany?: string | null;
  shipFromLine1?: string | null;
  shipFromLine2?: string | null;
  shipFromCity?: string | null;
  shipFromProvince?: string | null;
  shipFromPostalCode?: string | null;
}

export interface AddRmaItemInput {
  serializedItemId?: string;
  serialNumber?: string;
  sku: string;
  productName?: string;
  quantity: number;
  reason: RmaReason;
}

export interface UpdateRmaItemInput {
  reason?: RmaReason;
  conditionOnReceipt?: ItemCondition;
  itemResolution?: ItemResolution;
  itemNotes?: string;
}

export interface GenerateLabelInput {
  weight?: number;
  /** Defaults to 'canada_post' (existing authorized-return flow). */
  carrier?: 'canada_post' | 'planete_courrier';
  /** PC draft-first flow: the draft + priced service returned by GET /:id/rates. */
  draftId?: number;
  serviceAgreementId?: number;
  serviceName?: string;
  priceTotal?: number;
}

/** One row of the side-by-side return-rate comparison (GET /v4/rma/:id/rates). */
export interface RmaRateOption {
  carrier: 'canada_post' | 'planete_courrier';
  /** CP: DOM.EP & co. PC: the Galaxie serviceAgreementId (in serviceAgreementId). */
  serviceCode?: string;
  serviceAgreementId?: number;
  serviceName: string | null;
  priceTotal: number | null;
  /** CP authorized returns always carry delivery-confirmation handling; PC is NoSignature. */
  signatureRequired: boolean;
  expectedTransitTime?: string | null;
  expectedDeliveryDate?: string | null;
  deliveryEta?: string | null;
  deliveryEtaMessage?: string | null;
}

export interface RmaRatesResponse {
  weight: number;
  options: RmaRateOption[];
  pcDraftId: number;
  pcModifiedDate: string;
  warnings?: { canadaPost?: string };
}

// =============================================================================
// Quote -> RMA (auto-populate)
// =============================================================================

export type RmaMacOrigin = 'sensors_chain' | 'quote_device_links';

export interface RmaQuoteShipFrom {
  name: string | null;
  company: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
}

export interface RmaQuoteMaterialLine {
  sku: string;
  productName: string | null;
  quantity: number;
}

export interface RmaQuoteTransmitter {
  devId: string;
  transmitterName: string | null;
}

export interface RmaQuoteShippedDevice {
  devId: string;
  deviceType: string | null;
}

export interface RmaQuoteContext {
  quoteId: string;
  accountId: string | null;
  quoteNumber: string | null;
  shipFrom: RmaQuoteShipFrom;
  // The quote's SHIPPING site mongo id (may be null). Default-selects the
  // ship-from site in the account-sites picker; shipFrom is that same site.
  defaultSiteId?: string | null;
  materialLines: RmaQuoteMaterialLine[];
  resolvedTransmitters: RmaQuoteTransmitter[];
  shippedDevices: RmaQuoteShippedDevice[];
  sensorCount: number;
  distinctTransmitterCount: number;
  existingRmaCount: number;
}

export interface RmaFromQuoteItemInput {
  sku: string;
  productName?: string | null;
  quantity: number;
  serialNumber?: string | null; // the MAC / dev_id (always optional)
  reason?: RmaReason;
  macOrigin?: RmaMacOrigin;
}

export interface CreateRmaFromQuoteInput {
  accountId: string;
  quoteId?: string;
  quoteNumber?: string;
  source: 'shipping' | 'demo';
  reason?: RmaReason;
  cpServiceCode?: CpServiceCode;
  shipFromName?: string | null;
  shipFromCompany?: string | null;
  shipFromLine1?: string | null;
  shipFromLine2?: string | null;
  shipFromCity?: string | null;
  shipFromProvince?: string | null;
  shipFromPostalCode?: string | null;
  items: RmaFromQuoteItemInput[];
}

// =============================================================================
// Display Helpers
// =============================================================================

export const RMA_STATUS_LABELS: Record<RmaStatus, string> = {
  draft: 'Draft',
  label_generated: 'Label Generated',
  shipped: 'Shipped',
  received: 'Received',
  inspecting: 'Inspecting',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const RMA_REASON_LABELS: Record<RmaReason, string> = {
  defective: 'Defective',
  damaged: 'Damaged',
  wrong_item: 'Wrong Item',
  calibration: 'Calibration',
  end_of_life: 'End of Life',
  other: 'Other',
};

export const CP_SERVICE_LABELS: Record<CpServiceCode, string> = {
  'DOM.RP': 'Regular Parcel',
  'DOM.EP': 'Expedited Parcel',
  'DOM.XP': 'Xpresspost',
  'DOM.PC': 'Priority',
};
